import {
  Modal,
  SliderField,
  Typography /** Media query result is used within a client-only modal; this is safe to use. */,
  // eslint-disable-next-line no-restricted-imports
  useMobileMediaQuery,
} from '@iheartjane/reefer';
import type { MinMax } from '@iheartjane/types';
import { clsx } from 'clsx';
import isEqual from 'lodash-es/isEqual';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigation } from 'react-router';

import { ClientOnly } from '@jane/bloom/components/clientOnly';
import {
  useProductSearchFilters,
  useSearchParamFilter,
} from '@jane/bloom/hooks/filters';
import { FilterCategoryLabel } from '@jane/bloom/hooks/filters/useProductSearchFilters';
import { getFacetRange } from '@jane/bloom/viewModels/facetRange';
import type { SliderFilterKeys } from '@jane/bloom/viewModels/productSearchFilters';

import { DeselectAll } from './deselectAll';

import styles from './potencySection.module.css';

type PotencySliderProps = {
  filterKey: SliderFilterKeys;
  label: string;
  resetting?: boolean;
};

const PotencySlider = ({ filterKey, label, resetting }: PotencySliderProps) => {
  const isMobile = useMobileMediaQuery({});
  const { clearFilter, getFilterRangeValueWithBounds, setFilterRange } =
    useSearchParamFilter(filterKey);
  const { getProductSearchFilterOptions } = useProductSearchFilters();
  const filterList = getProductSearchFilterOptions(filterKey);
  const { state } = useNavigation();
  const [deselecting, setDeselecting] = useState(false);

  const { rangeMax, rangeMin } = getFacetRange(filterList);
  const defaultRange = useMemo(
    () => ({
      max: rangeMax,
      min: rangeMin,
    }),
    [rangeMax, rangeMin]
  );
  const [displayValue, setDisplayValue] = useState(
    getFilterRangeValueWithBounds(defaultRange)
  );

  const hasValidValues =
    filterList.filter((filter) => filter.count > 0).length > 1 &&
    rangeMax - rangeMin > 1;

  const handleResetPotency = useCallback(
    (
      option:
        'reset-display' | 'reset-display-and-url' = 'reset-display-and-url'
    ) =>
      () => {
        setDeselecting(true);
        setDisplayValue(defaultRange);

        if (option === 'reset-display-and-url') {
          clearFilter();
        }
      },
    [clearFilter, defaultRange, setDeselecting, setDisplayValue]
  );

  const onAfterSliderChange = (val: MinMax) => {
    const isDefaultRange = isEqual(defaultRange, displayValue);

    if (!isDefaultRange && displayValue.min === 0) {
      const filterableMin = 0.000001;

      setFilterRange({
        max: displayValue.max,
        min: filterableMin,
      });
    } else {
      setFilterRange(val);
    }
  };

  useEffect(() => {
    if (deselecting && state === 'idle') {
      setDeselecting(false);
    }
  }, [deselecting, state]);

  useEffect(() => {
    if (resetting) {
      // When filters are cleared from the surrounding modal, the data model is updated automatically.
      // This component just needs to reset its display values.
      handleResetPotency('reset-display')();
    }
  }, [handleResetPotency, resetting]);

  return (
    <div>
      <div className={styles['header']}>
        <Typography
          className={styles['title']}
          variant={isMobile ? 'body-regular' : 'h2-semibold'}
        >
          {label}
        </Typography>
        {hasValidValues && <DeselectAll onClick={handleResetPotency()} />}
      </div>
      <SliderField
        className={clsx(styles['slider'], {
          [styles['slider--hidden-values']]: !hasValidValues,
        })}
        disabled={!hasValidValues}
        label={label}
        labelHidden
        endUnit="%"
        defaultValue={displayValue}
        value={displayValue}
        max={rangeMax}
        min={rangeMin}
        onChange={setDisplayValue}
        onAfterChange={onAfterSliderChange}
        isDouble
        name={label}
      />
    </div>
  );
};

interface PotencySectionProps {
  resetting?: boolean;
}

export const PotencySection = ({ resetting }: PotencySectionProps) => (
  <>
    <div className={styles['potencySection']}>
      <PotencySlider
        label={FilterCategoryLabel['thcPotencies']}
        filterKey="thcPotencies"
        resetting={resetting}
      />
      <ClientOnly>
        {(isMobile) =>
          isMobile && (
            <Modal.ContentDivider height={16} color="grays-ultralight" />
          )
        }
      </ClientOnly>
      <PotencySlider
        label={FilterCategoryLabel['cbdPotencies']}
        filterKey="cbdPotencies"
        resetting={resetting}
      />
    </div>
    <ClientOnly>
      {() => <Modal.ContentDivider height={16} color="grays-ultralight" />}
    </ClientOnly>
  </>
);
